import React, { useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Grid, Icon, Popup } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import InfoPopup from '~/components/InfoPopup'
import TableRearing from './TableRearing'
import { StyledButton, StyledField } from '../style'
import LinkPopup from '../LinkPopup'
import { useParams } from 'react-router-dom'
import { formatNumber, handleErrors } from '~/utils/helpers'
import { StyledGridColumn } from './style'
import { NumberKeyLabelType } from '~/types'
import CO2Service from '~/api/services/co2'
interface Props {
  calfRearingTypes: NumberKeyLabelType[]
  setIsLoadingResults: (value: boolean) => void
}

const FourthStep: React.FC<Props> = (props) => {
  const { calfRearingTypes, setIsLoadingResults } = props
  const { control, setValue, clearErrors, getValues, reset } = useFormContext()

  const watchCalfRearing = useWatch({ control, name: 'calfRearing' })
  const watchCalfWeightFourWeeks = useWatch({ control, name: 'calfWeightAtFourWeeks' })
  const watchCalvesStillUnborn = useWatch({ control, name: 'calvesStillbornAndDead' })
  const watchTaxField = useWatch({ control, name: 'tax' })
  const watchCowsNumber = useWatch({ control, name: 'cowsNumber' })
  const watchLiveBornCalves = useWatch({ control, name: 'liveBornCalves' })
  const watchMilkReplacerCon = useWatch({ control, name: 'milkReplacerConcentration' })

  const { id, farmerId } = useParams()

  const [isLoadingPredefinedData, setIsLoadingPredefinedData] = useState(false)

  const weaningOptions = Array.from({ length: 13 }, (_, i) => {
    const value = i + 4;
    return { value, text: `${String(value)} Wochen` };
  });

  async function fetchPredefinedData(calfRearing) {
    setIsLoadingPredefinedData(true)
    setIsLoadingResults(true)
    try {
      const formValues = getValues()
      const response = await CO2Service.getCowPredData(id, {
        breeds: formValues.breeds,
        tax: watchTaxField,
        farmerId,
        calfRearing: calfRearing
      })
      reset({
        ...formValues,
        weaningWeeks: 12,
        weaningWeeksValues: response.data.weaningWeeksValues,
        milkReplacerConcentration: '',
        calfRearing
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingPredefinedData(false)
    setIsLoadingResults(false)
  }

  return <Grid>
    <Grid.Row columns={5} style={{ paddingBottom: 0 }}>
      <StyledGridColumn>
        <FormFields.Input
          control={control}
          name={'liveBornCalves'}
          disabled={isLoadingPredefinedData}
          noErrorMessage={!watchLiveBornCalves}
          suffix={' Kälber'}
          styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em', maxWidth: 300 }}
          withoutAsterisk
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{'Lebendgeburten'}</label>
            <InfoPopup
              content={'Wie viele Kälber wurden lebend geboren? Berücksichtigen Sie bitte weibliche und männliche Kälber.'}
              styleIcon={{ fontSize: '1.2em' }}
              position={'top center'}
            />
            <LinkPopup
              content={'HITier'}
            />
            {watchLiveBornCalves > (watchCowsNumber * 1.5) && <Popup
              content={'Die Anzahl der Lebendgeburten liegt in einem deutlich überdurchschnittlichen Bereich.'}
              trigger={<Icon name='warning sign' color='yellow' style={{ fontSize: '1.2em' }} />}
            />}
          </div>}
          typeInput='controled'
          rules={{
            required: true,
            min: 0,
            max: {
              value: watchCowsNumber ? Math.floor(parseInt(watchCowsNumber) * 2) : null,
              message: `Die Anzahl der Lebendgeburten darf maximal das 2-fache des ⌀-Bestands Ihrer Milchkühe ${formatNumber(Math.floor(parseInt(watchCowsNumber) * 2), 0)} betragen.`
            }
          }}
          placeholder={'Anzahl lebendig geborene Kälber'}
        />
      </StyledGridColumn>

      <StyledGridColumn>
        <FormFields.Input
          control={control}
          name={'calvesStillbornAndDead'}
          disabled={isLoadingPredefinedData}
          withoutAsterisk
          rules={{
            validate: (value) => {
              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
              if (transformValue < 0 || transformValue > 40) {
                return false
              }
              return true
            },
            required: true
          }}
          noErrorMessage
          suffix=' %'
          typeInput={'controled'}
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{'Kälberverluste bis 28. LT'}</label>
            <InfoPopup
              content={`Anteil der Kälberverluste zwischen Geburt und 28. Lebenstag. Die Verluste umfassen Totgeburten, Verendungen, Tötungen. 
                Berücksichtigen Sie bitte weibliche und männliche Kälber.`}
              styleIcon={{ fontSize: '1.2em' }}
              position={'top center'}
            />
            <LinkPopup
              content={'LKV-Bericht; Q-Check Daten; Herdenmanagementprogramm'}
            />
            {watchCalvesStillUnborn > 30 && <Popup
              content={'Der prozentuale Anteil der Kälberverluste liegt über dem Normalbereich von 30%.'}
              trigger={<Icon name='warning sign' color='yellow' style={{ fontSize: '1.2em' }} />}
            />}
          </div>}
          placeholder={'in %'}
        />
      </StyledGridColumn>

      <StyledGridColumn>
        <StyledField>
          <label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div>{'Gewicht der Kälber (am 28 LT)'}</div>
              <InfoPopup
                content={`Berücksichtigen Sie bitte weibliche und männliche Kälber.
                  Sie können hier das Geburtsgewicht mit der Tageszunahme überschlagen. 
                  Das Geburtsgewicht eines Kalbs liegt ca. zwischen 40 und 45 kg. 
                  Bei einer Tageszunahme von 1 kg/Tag läge das Gewicht am 28. LT zwischen 68 und 73 kg. 
                  Jerseykälber wiegen ca 30 kg, bei Tageszunahmen von 550 g.`}
                styleIcon={{ fontSize: '1.2em' }}
                position={'top center'}
              />
            </div>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8, width: 300 }}>
            <StyledButton
              type='button'
              icon='minus'
              disabled={(watchCalfWeightFourWeeks - 5 <= 0 || !watchCalfWeightFourWeeks) || isLoadingPredefinedData}
              onClick={() => {
                if (!watchCalfWeightFourWeeks) {
                  return
                }
                const numberCalfWeight = parseFloat(watchCalfWeightFourWeeks) - 5
                if (numberCalfWeight >= 30 && numberCalfWeight <= 200) {
                  clearErrors('calfWeightAtFourWeeks')
                }
                setValue('calfWeightAtFourWeeks', numberCalfWeight)
              }}
            />
            <FormFields.Input
              control={control}
              name={'calfWeightAtFourWeeks'}
              suffix={' kg Lebendmasse'}
              withoutAsterisk
              typeInput='controled'
              allowDecimals={true}
              noErrorMessage
              decimalScale={1}
              rules={{
                validate: (value) => {
                  const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                  if (transformValue < 30 || transformValue > 200) {
                    return false
                  }
                  return true
                }
              }}
              placeholder={'in kg Lebendmasse'}
            />
            <StyledButton
              type='button'
              disabled={isLoadingPredefinedData}
              onClick={() => {
                const numberCalfWeight = parseFloat(watchCalfWeightFourWeeks || 0) + 5
                if (numberCalfWeight >= 30 && numberCalfWeight <= 200) {
                  clearErrors('calfWeightAtFourWeeks')
                }
                setValue('calfWeightAtFourWeeks', numberCalfWeight)
              }}
              icon='add'
            />
          </div>
        </StyledField>
      </StyledGridColumn>
    </Grid.Row>

    <Grid.Row columns={5}>
      <StyledGridColumn>
        <FormFields.Select
          options={calfRearingTypes?.map(type => ({ value: type.key, text: type.label }))}
          selectOnBlur={false}
          disabled={isLoadingPredefinedData}
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div>{'Tränkekurve'}</div>
            <InfoPopup
              content={'Bitte geben Sie an, ob Sie Vollmilch, Milchaustaushcer (MAT) oder beides vertränkt haben.'}
              styleIcon={{ fontSize: '1.2em' }}
              position={'top center'}
            />
          </label>}
          control={control}
          onChange={(e, data) => {
            setValue('calfRearing', data.value)
            fetchPredefinedData(data.value)
          }}
          placeholder={'Bitte auswählen'}
          rules={{
            required: true
          }}
          withoutAsterisk
          noErrorMessage
          name='calfRearing'
        />
      </StyledGridColumn>
      <StyledGridColumn>
        <FormFields.Select
          options={weaningOptions}
          disabled={isLoadingPredefinedData}
          selectOnBlur={false}
          label={'Absetzalter'}
          control={control}
          placeholder={'Bitte auswählen'}
          rules={{
            required: true
          }}
          withoutAsterisk
          noErrorMessage
          name='weaningWeeks'
        />
      </StyledGridColumn>
      {watchCalfRearing !== 2 && <StyledGridColumn>
        <FormFields.Input
          control={control}
          name={'milkReplacerConcentration'}
          disabled={isLoadingPredefinedData}
          noErrorMessage={!watchMilkReplacerCon}
          styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em', maxWidth: 300 }}
          typeInput='controled'
          withoutAsterisk
          rules={{
            required: true,
            validate: (value) => {
              const transformedValue = parseInt(value)
              if (transformedValue < 90 || transformedValue > 300) {
                return 'Der Wert für die MAT-Konzentration muss mindestens 90 g/Liter und darf maximal 300 g/Liter betragen.'
              }
              return true
            }
          }}
          suffix=' g / Liter'
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div>{'MAT-Konzentration'}</div>
            <InfoPopup
              content={'Bitte geben Sie hier die angewendete Konzentration Ihrer Milchaustauscher-Tränke in g/l Wasser an.'}
              styleIcon={{ fontSize: '1.2em' }}
              position={'top center'}
            />
          </label>}
          placeholder={'g / Liter'}
        />
      </StyledGridColumn>}
    </Grid.Row>
    <Grid.Row>
      <StyledGridColumn width={10}>
        <TableRearing
          calfRearingTypes={[...calfRearingTypes]}
          isLoadingPredefinedData={isLoadingPredefinedData}
        />
      </StyledGridColumn>
    </Grid.Row>
  </Grid>
}
export default FourthStep